import { Outlet, createRoute } from "@tanstack/react-router";
import { rootRoute } from "./root";
import { PrototypesIndex } from "@/prototypes";
import { RoomsListVariantA } from "@/prototypes/rooms-list/a";
import { RoomsListVariantB } from "@/prototypes/rooms-list/b";
import { RoomsListVariantC } from "@/prototypes/rooms-list/c";
import { RoomCreateVariantA } from "@/prototypes/room-create/a";
import { RoomCreateVariantB } from "@/prototypes/room-create/b";
import { RoomCreateVariantC } from "@/prototypes/room-create/c";
import { MargauxPromptVariantA } from "@/prototypes/margaux-prompt/a";
import { MargauxPromptVariantB } from "@/prototypes/margaux-prompt/b";
import { MargauxPromptVariantC } from "@/prototypes/margaux-prompt/c";
import { MultiClubHomeVariantV1 } from "@/prototypes/multi-club-home/v1";
import { MultiClubHomeVariantV2 } from "@/prototypes/multi-club-home/v2";
import { MultiClubHomeVariantV3 } from "@/prototypes/multi-club-home/v3";
import { ClubHomeVariantV1 } from "@/prototypes/club-home/v1";
import { ClubHomeVariantV2 } from "@/prototypes/club-home/v2";
import { ClubHomeVariantV3 } from "@/prototypes/club-home/v3";
import { GatheringsVariantV1 } from "@/prototypes/gatherings/v1";
import { GatheringsVariantV2 } from "@/prototypes/gatherings/v2";
import { GatheringsVariantV3 } from "@/prototypes/gatherings/v3";
import { VotingVariantV1 } from "@/prototypes/voting/v1";
import { VotingVariantV2 } from "@/prototypes/voting/v2";
import { VotingVariantV3 } from "@/prototypes/voting/v3";
import { RoomCreateVariantV1 } from "@/prototypes/room-create/v1";
import { RoomCreateVariantV2 } from "@/prototypes/room-create/v2";
import { RoomCreateVariantV3 } from "@/prototypes/room-create/v3";
import { FacilitatorVariantV1 } from "@/prototypes/facilitator/v1";
import { FacilitatorVariantV2 } from "@/prototypes/facilitator/v2";
import { FacilitatorVariantV3 } from "@/prototypes/facilitator/v3";
import { MarketingVariantV1 } from "@/prototypes/marketing/v1";
import { MarketingVariantV2 } from "@/prototypes/marketing/v2";
import { MarketingVariantV3 } from "@/prototypes/marketing/v3";
import { DiscoverVariantV1 } from "@/prototypes/discover/v1";
import { DiscoverVariantV2 } from "@/prototypes/discover/v2";
import { DiscoverVariantV3 } from "@/prototypes/discover/v3";
import { GatheringDetailVariantV1 } from "@/prototypes/gathering-detail/v1";
import { GatheringDetailVariantV2 } from "@/prototypes/gathering-detail/v2";
import { GatheringDetailVariantV3 } from "@/prototypes/gathering-detail/v3";
import { WelcomeHubVariantV1 } from "@/prototypes/welcome-hub/v1";
import { WelcomeHubVariantV2 } from "@/prototypes/welcome-hub/v2";
import { WelcomeHubVariantV3 } from "@/prototypes/welcome-hub/v3";
import { UserMenuVariantV1 } from "@/prototypes/user-menu/v1";
import { UserMenuVariantV2 } from "@/prototypes/user-menu/v2";
import { UserMenuVariantV3 } from "@/prototypes/user-menu/v3";
import { NotFound } from "@/components/NotFound";

/**
 * The prototypes route group only renders in development. In production
 * builds Vite aliases `@/prototypes` to a stub (see `vite.config.ts`), and
 * we gate at runtime via `import.meta.env.DEV` as defence in depth.
 */
function PrototypesShell() {
  if (!import.meta.env.DEV) return <NotFound />;
  return <Outlet />;
}

const prototypesParent = createRoute({
  getParentRoute: () => rootRoute,
  path: "/prototypes",
  component: PrototypesShell,
});

const prototypesIndexRoute = createRoute({
  getParentRoute: () => prototypesParent,
  path: "/",
  component: PrototypesIndex,
});

const roomsListAroute = createRoute({
  getParentRoute: () => prototypesParent,
  path: "/rooms-list/a",
  component: RoomsListVariantA,
});
const roomsListBroute = createRoute({
  getParentRoute: () => prototypesParent,
  path: "/rooms-list/b",
  component: RoomsListVariantB,
});
const roomsListCroute = createRoute({
  getParentRoute: () => prototypesParent,
  path: "/rooms-list/c",
  component: RoomsListVariantC,
});

const roomCreateAroute = createRoute({
  getParentRoute: () => prototypesParent,
  path: "/room-create/a",
  component: RoomCreateVariantA,
});
const roomCreateBroute = createRoute({
  getParentRoute: () => prototypesParent,
  path: "/room-create/b",
  component: RoomCreateVariantB,
});
const roomCreateCroute = createRoute({
  getParentRoute: () => prototypesParent,
  path: "/room-create/c",
  component: RoomCreateVariantC,
});

const margauxPromptAroute = createRoute({
  getParentRoute: () => prototypesParent,
  path: "/margaux-prompt/a",
  component: MargauxPromptVariantA,
});
const margauxPromptBroute = createRoute({
  getParentRoute: () => prototypesParent,
  path: "/margaux-prompt/b",
  component: MargauxPromptVariantB,
});
const margauxPromptCroute = createRoute({
  getParentRoute: () => prototypesParent,
  path: "/margaux-prompt/c",
  component: MargauxPromptVariantC,
});

/* Phase 2 — organizer cockpit prototypes ----------------------------- */

const multiClubHomeV1Route = createRoute({
  getParentRoute: () => prototypesParent,
  path: "/multi-club-home/v1",
  component: MultiClubHomeVariantV1,
});
const multiClubHomeV2Route = createRoute({
  getParentRoute: () => prototypesParent,
  path: "/multi-club-home/v2",
  component: MultiClubHomeVariantV2,
});
const multiClubHomeV3Route = createRoute({
  getParentRoute: () => prototypesParent,
  path: "/multi-club-home/v3",
  component: MultiClubHomeVariantV3,
});

const clubHomeV1Route = createRoute({
  getParentRoute: () => prototypesParent,
  path: "/club-home/v1",
  component: ClubHomeVariantV1,
});
const clubHomeV2Route = createRoute({
  getParentRoute: () => prototypesParent,
  path: "/club-home/v2",
  component: ClubHomeVariantV2,
});
const clubHomeV3Route = createRoute({
  getParentRoute: () => prototypesParent,
  path: "/club-home/v3",
  component: ClubHomeVariantV3,
});

const gatheringsV1Route = createRoute({
  getParentRoute: () => prototypesParent,
  path: "/gatherings/v1",
  component: GatheringsVariantV1,
});
const gatheringsV2Route = createRoute({
  getParentRoute: () => prototypesParent,
  path: "/gatherings/v2",
  component: GatheringsVariantV2,
});
const gatheringsV3Route = createRoute({
  getParentRoute: () => prototypesParent,
  path: "/gatherings/v3",
  component: GatheringsVariantV3,
});

const votingV1Route = createRoute({
  getParentRoute: () => prototypesParent,
  path: "/voting/v1",
  component: VotingVariantV1,
});
const votingV2Route = createRoute({
  getParentRoute: () => prototypesParent,
  path: "/voting/v2",
  component: VotingVariantV2,
});
const votingV3Route = createRoute({
  getParentRoute: () => prototypesParent,
  path: "/voting/v3",
  component: VotingVariantV3,
});

const roomCreateV1Route = createRoute({
  getParentRoute: () => prototypesParent,
  path: "/room-create/v1",
  component: RoomCreateVariantV1,
});
const roomCreateV2Route = createRoute({
  getParentRoute: () => prototypesParent,
  path: "/room-create/v2",
  component: RoomCreateVariantV2,
});
const roomCreateV3Route = createRoute({
  getParentRoute: () => prototypesParent,
  path: "/room-create/v3",
  component: RoomCreateVariantV3,
});

const facilitatorV1Route = createRoute({
  getParentRoute: () => prototypesParent,
  path: "/facilitator/v1",
  component: FacilitatorVariantV1,
});
const facilitatorV2Route = createRoute({
  getParentRoute: () => prototypesParent,
  path: "/facilitator/v2",
  component: FacilitatorVariantV2,
});
const facilitatorV3Route = createRoute({
  getParentRoute: () => prototypesParent,
  path: "/facilitator/v3",
  component: FacilitatorVariantV3,
});

/* Phase 4 — public web + chrome unification prototypes ---------------- */

const marketingV1Route = createRoute({
  getParentRoute: () => prototypesParent,
  path: "/marketing/v1",
  component: MarketingVariantV1,
});
const marketingV2Route = createRoute({
  getParentRoute: () => prototypesParent,
  path: "/marketing/v2",
  component: MarketingVariantV2,
});
const marketingV3Route = createRoute({
  getParentRoute: () => prototypesParent,
  path: "/marketing/v3",
  component: MarketingVariantV3,
});

const discoverV1Route = createRoute({
  getParentRoute: () => prototypesParent,
  path: "/discover/v1",
  component: DiscoverVariantV1,
});
const discoverV2Route = createRoute({
  getParentRoute: () => prototypesParent,
  path: "/discover/v2",
  component: DiscoverVariantV2,
});
const discoverV3Route = createRoute({
  getParentRoute: () => prototypesParent,
  path: "/discover/v3",
  component: DiscoverVariantV3,
});

const gatheringDetailV1Route = createRoute({
  getParentRoute: () => prototypesParent,
  path: "/gathering-detail/v1",
  component: GatheringDetailVariantV1,
});
const gatheringDetailV2Route = createRoute({
  getParentRoute: () => prototypesParent,
  path: "/gathering-detail/v2",
  component: GatheringDetailVariantV2,
});
const gatheringDetailV3Route = createRoute({
  getParentRoute: () => prototypesParent,
  path: "/gathering-detail/v3",
  component: GatheringDetailVariantV3,
});

const welcomeHubV1Route = createRoute({
  getParentRoute: () => prototypesParent,
  path: "/welcome-hub/v1",
  component: WelcomeHubVariantV1,
});
const welcomeHubV2Route = createRoute({
  getParentRoute: () => prototypesParent,
  path: "/welcome-hub/v2",
  component: WelcomeHubVariantV2,
});
const welcomeHubV3Route = createRoute({
  getParentRoute: () => prototypesParent,
  path: "/welcome-hub/v3",
  component: WelcomeHubVariantV3,
});

const userMenuV1Route = createRoute({
  getParentRoute: () => prototypesParent,
  path: "/user-menu/v1",
  component: UserMenuVariantV1,
});
const userMenuV2Route = createRoute({
  getParentRoute: () => prototypesParent,
  path: "/user-menu/v2",
  component: UserMenuVariantV2,
});
const userMenuV3Route = createRoute({
  getParentRoute: () => prototypesParent,
  path: "/user-menu/v3",
  component: UserMenuVariantV3,
});

export const prototypesRoute = prototypesParent.addChildren([
  prototypesIndexRoute,
  roomsListAroute,
  roomsListBroute,
  roomsListCroute,
  roomCreateAroute,
  roomCreateBroute,
  roomCreateCroute,
  margauxPromptAroute,
  margauxPromptBroute,
  margauxPromptCroute,
  multiClubHomeV1Route,
  multiClubHomeV2Route,
  multiClubHomeV3Route,
  clubHomeV1Route,
  clubHomeV2Route,
  clubHomeV3Route,
  gatheringsV1Route,
  gatheringsV2Route,
  gatheringsV3Route,
  votingV1Route,
  votingV2Route,
  votingV3Route,
  roomCreateV1Route,
  roomCreateV2Route,
  roomCreateV3Route,
  facilitatorV1Route,
  facilitatorV2Route,
  facilitatorV3Route,
  marketingV1Route,
  marketingV2Route,
  marketingV3Route,
  discoverV1Route,
  discoverV2Route,
  discoverV3Route,
  gatheringDetailV1Route,
  gatheringDetailV2Route,
  gatheringDetailV3Route,
  welcomeHubV1Route,
  welcomeHubV2Route,
  welcomeHubV3Route,
  userMenuV1Route,
  userMenuV2Route,
  userMenuV3Route,
]);
